import { useState, useEffect } from "react";
import { Button } from "../../../shadcn/components/ui/Button";
import { Input } from "../../../shadcn/components/ui/Input";
import { Calendar } from "../../../shadcn/components/ui/Calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../shadcn/components/ui/Popover";
import { CalendarIcon, Search } from "lucide-react";
import { format } from "date-fns";
import { cn } from "../../../shadcn/lib/utils";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export function Applications() {
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState();
  const [dateTo, setDateTo] = useState();
  const [applications, setApplications] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState({
    pending: false,
    approved: false,
    rejected: false,
  });
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();

  // Fetch applications from the API
  useEffect(() => {
    const fetchForms = async () => {
      try {
        console.log("Fetching rental and lease forms...");

        // Fetch Rental Data
        const rentalResponse = await axios.post(
          "https://silver-tlc-backend-delta.vercel.app/api/v1/propertypurchaseform/rentalapplication-request/get-propertyowner",
          { token }
        );
        console.log("✅ Rental API Response:", rentalResponse.data);

        // Process Rental Data
        const rentalForms = Array.isArray(rentalResponse.data?.RentalFormData)
          ? rentalResponse.data.RentalFormData.map((form) => ({
              ...form,
              type: "Rental",
            }))
          : [];

        console.log("🔹 Processed Rental Forms:", rentalForms);

        let leaseForms = [];

        // Fetch Lease Data (Handle 404 Errors)
        try {
          const leaseResponse = await axios.post(
            "https://silver-tlc-backend-delta.vercel.app/api/v1/propertypurchaseform/leaseform/get-propertyowner",
            { token }
          );
          console.log("✅ Lease API Response:", leaseResponse.data.LeaseFormData);

          // Process Lease Data
          leaseForms = Array.isArray(leaseResponse.data?.LeaseFormData)
            ? leaseResponse.data.LeaseFormData.map((form) => ({
                ...form,
                type: "Lease",
              }))
            : [];
        } catch (leaseError) {
          if (leaseError.response?.status === 404) {
            console.warn("⚠️ Lease API returned 404, continuing without lease data.");
          } else {
            console.error("❌ Lease API Error:", leaseError.message);
          }
        }

        // Combine Data
        const allApplications = [...leaseForms, ...rentalForms];

        console.log(allApplications)
        setApplications(allApplications);
      } catch (error) {
        console.error("❌ Error fetching forms:", error.response?.data || error.message);
      }
    };

    fetchForms();
  }, [token]);

  // Handle Accept/Reject
  const handleAcceptReject = async (formId, approved, reject) => {
    try {
      const response = await axios.post(
        "https://silver-tlc-backend-delta.vercel.app/api/v1/propertypurchaseform/rentalapplication-request/approved-propertyowner",
        {
          token,
          form_id: formId,
          approved,
          reject,
        }
      );

      if (response.data.success) {
        // Update local state
        setApplications((prevApplications) =>
          prevApplications.map((app) =>
            app._id === formId
              ? { ...app, approved, reject }
              : app
          )
        );
      }
    } catch (error) {
      console.error("Error updating application status:", error);
    }
  };

  // Handle status filter change
  const handleStatusChange = (status) => {
    setSelectedStatuses((prev) => ({
      ...prev,
      [status]: !prev[status],
    }));
  };

  // Reset all filters
  const handleReset = () => {
    setDateFrom(undefined);
    setDateTo(undefined);
    setSelectedStatuses({
      pending: false,
      approved: false,
      rejected: false,
    });
  };

  // Navigate to the form details page
  const handleFormClick = (id, type) => {
    const route =
      type === "Lease"
        ? `/propertyownerdashboard/filledlease/${id}`
        : `/propertyownerdashboard/filledrental/${id}`;
    navigate(route);
  };

  // Filter applications based on selected filters
  const filteredApplications = applications.filter((app) => {
    const submissionDate = new Date(app.createdAt);
    const isDateInRange =
      (!dateFrom || submissionDate >= dateFrom) &&
      (!dateTo || submissionDate <= dateTo);

    const status = app.approved ? "approved" : app.reject ? "rejected" : "pending";
    const isStatusMatch =
      (!selectedStatuses.pending && !selectedStatuses.approved && !selectedStatuses.rejected) ||
      (selectedStatuses.pending && status === "pending") ||
      (selectedStatuses.approved && status === "approved") ||
      (selectedStatuses.rejected && status === "rejected");

    const isSearchMatch =
      !searchQuery ||
      (app.name && app.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (app.type && app.type.toLowerCase().includes(searchQuery.toLowerCase()));

    return isDateInRange && isStatusMatch && isSearchMatch;
  });

  return (
    <div className="flex flex-col md:flex-row gap-6 p-0 lg:p-6">
      {/* Filters Section */}
      <div className="w-full md:w-64 space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="font-semibold">Filters</h3>
        </div>

        <div className="space-y-4">
          {/* Date Range Filter */}
          <div>
            <h4 className="text-sm font-medium mb-2">Date Range</h4>
            <div className="space-y-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dateFrom && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateFrom ? format(dateFrom, "PPP") : <span>Date from</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={dateFrom}
                    onSelect={setDateFrom}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dateTo && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateTo ? format(dateTo, "PPP") : <span>Date to</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={dateTo}
                    onSelect={setDateTo}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <h4 className="text-sm font-medium mb-2">Status</h4>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedStatuses.pending}
                  onChange={() => handleStatusChange("pending")}
                  className="rounded border-gray-300"
                />
                <span>Pending</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedStatuses.approved}
                  onChange={() => handleStatusChange("approved")}
                  className="rounded border-gray-300"
                />
                <span>Approved</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedStatuses.rejected}
                  onChange={() => handleStatusChange("rejected")}
                  className="rounded border-gray-300"
                />
                <span>Rejected</span>
              </label>
            </div>
          </div>
        </div>

        {/* Reset Button */}
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset} className="flex-1">
            Reset
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1">
        <div className="lg:mb-6 mb-2 mt-5 lg:mt-0">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 w-[20px] -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4"
            />
          </div>
        </div>

        <div className="rounded-lg border bg-card">
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">
              Application Review Overview
            </h2>
            <div className="bg-red-100 p-6 rounded-lg mb-6">
              <div className="text-center">
                <div className="text-sm text-gray-600 mb-1">
                  TOTAL APPLICATIONS
                </div>
                <div className="text-4xl font-bold">
                  {filteredApplications.length}
                </div>
              </div>
            </div>

            {/* Applications Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredApplications.map((app) => (
                <div key={app._id} className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-medium">{app.name}</h3>
                    <span className="text-sm bg-gray-200 px-2 py-1 rounded">
                      {app.type}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Application Type</span>
                      <span>{app.type}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Submission Date</span>
                      <span>{app.createdAt.split("T")[0]}</span>
                    </div>

                    {/* Status and Open Button */}
                    <div className="flex flex-col md:flex-row items-center justify-between gap-2 w-full">
                      <Button
                        className="w-full md:w-auto mt-2 rounded-full bg-red-100 hover:bg-red-200 text-red-600"
                        variant="ghost"
                        onClick={() => handleFormClick(app._id, app.type)}
                      >
                        Open
                      </Button>

                      {/* Conditionally Render Accept/Reject Buttons or Status Text */}
                      {app.type === "Rental" && (
                        <>
                          {!app.approved && !app.reject ? (
                            <div className="flex gap-2">
                              <Button
                                className="bg-green-500 hover:bg-green-600 text-white"
                                onClick={() => handleAcceptReject(app._id, true, false)}
                              >
                                Accept
                              </Button>
                              <Button
                                className="bg-red-500 hover:bg-red-600 text-white"
                                onClick={() => handleAcceptReject(app._id, false, true)}
                              >
                                Reject
                              </Button>
                            </div>
                          ) : (
                            <div
                              className={`px-4 py-1 rounded-full text-white text-center w-full md:w-20 ${
                                app.approved
                                  ? "bg-green-500"
                                  : app.reject
                                  ? "bg-red-500"
                                  : "bg-yellow-500"
                              }`}
                            >
                              {app.approved
                                ? "Accepted"
                                : app.reject
                                ? "Rejected"
                                : "Pending"}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}